/**
 * UserActionModals — 5 user-management dialogs extracted from ManagePlatform.js.
 *   - EditUserModal, ResetPasswordModal, DeleteUserModal, AddUserModal, ResetLinkModal
 */
import { Edit3, Key, Trash2, UserPlus, Link2, Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';

export function EditUserModal({
  open, onOpenChange, selectedUser,
  editUserData, setEditUserData,
  onCancel, onSubmit, processingUser,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="user-edit-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-blue-500" />
            تعديل بيانات المستخدم
          </DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div>
            <label className="text-sm text-[#808080] mb-2 block">الاسم</label>
            <Input
              value={editUserData.name}
              onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
              placeholder="اسم المستخدم"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
            />
          </div>
          <div>
            <label className="text-sm text-[#808080] mb-2 block">البريد الإلكتروني</label>
            <Input
              value={editUserData.email}
              onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
              placeholder="البريد الإلكتروني"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              dir="ltr"
            />
          </div>
          <div>
            <label className="text-sm text-[#808080] mb-2 block">رقم الهاتف</label>
            <Input
              value={editUserData.phone}
              onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })}
              placeholder="رقم الهاتف"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              dir="ltr"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="border-[#2a2a4e] text-white">
            إلغاء
          </Button>
          <Button
            onClick={onSubmit}
            disabled={processingUser === selectedUser?.id}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="user-edit-submit-btn"
          >
            {processingUser === selectedUser?.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <CheckCircle className="h-4 w-4 me-2" />
                حفظ التغييرات
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetPasswordModal({
  open, onOpenChange, selectedUser,
  newPassword, setNewPassword,
  onCancel, onSubmit, processingUser,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="user-reset-password-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Key className="h-5 w-5 text-amber-500" />
            إعادة تعيين كلمة المرور
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-[#808080] mb-4">
            إعادة تعيين كلمة المرور للمستخدم: <span className="text-white font-medium">{selectedUser?.name || selectedUser?.email}</span>
          </p>
          <div>
            <label className="text-sm text-[#808080] mb-2 block">كلمة المرور الجديدة *</label>
            <Input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="أدخل كلمة المرور الجديدة (6 أحرف على الأقل)"
              className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
              dir="ltr"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="border-[#2a2a4e] text-white">
            إلغاء
          </Button>
          <Button
            onClick={onSubmit}
            disabled={!newPassword.trim() || newPassword.length < 6 || processingUser === selectedUser?.id}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="user-reset-password-submit-btn"
          >
            {processingUser === selectedUser?.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Key className="h-4 w-4 me-2" />
                تعيين كلمة المرور
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteUserModal({ open, onOpenChange, selectedUser, onCancel, onSubmit, processingUser }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e] text-white" data-testid="user-delete-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-red-500" />
            حذف الحساب
          </DialogTitle>
          <DialogDescription className="text-[#808080]">هذا الإجراء لا يمكن التراجع عنه</DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400">
              هل أنت متأكد من حذف حساب{' '}
              <span className="font-bold text-white">{selectedUser?.name || selectedUser?.email}</span>؟
            </p>
            <p className="text-sm text-[#808080] mt-2">سيتم حذف جميع بيانات هذا المستخدم بشكل نهائي.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} className="border-[#2a2a4e] text-white">
            إلغاء
          </Button>
          <Button
            onClick={onSubmit}
            disabled={processingUser === selectedUser?.id}
            className="bg-red-600 hover:bg-red-700 text-white"
            data-testid="user-delete-submit-btn"
          >
            {processingUser === selectedUser?.id ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Trash2 className="h-4 w-4 me-2" />
                نعم، احذف الحساب
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddUserModal({
  open, onOpenChange, language,
  newUserData, setNewUserData,
  onCancel, onSubmit, processingUser,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0a0a0a] border border-white/10 text-white max-w-md" data-testid="user-add-modal">
        <DialogHeader>
          <DialogTitle className="text-emerald-400 flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {language === 'ar' ? 'إضافة عميل جديد' : 'Add New Customer'}
          </DialogTitle>
          <DialogDescription className="text-[#71717A]">
            {language === 'ar'
              ? 'سيتم إرسال رابط تفعيل الحساب للعميل عبر البريد الإلكتروني'
              : 'An account activation link will be sent to the customer via email'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-2">
                {language === 'ar' ? 'الاسم الأول' : 'First Name'} *
              </label>
              <Input
                value={newUserData.first_name}
                onChange={(e) => setNewUserData({ ...newUserData, first_name: e.target.value })}
                placeholder={language === 'ar' ? 'أحمد' : 'Ahmed'}
                className="bg-white/5 border-white/10 text-white focus:border-emerald-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#71717A] mb-2">
                {language === 'ar' ? 'الاسم الأخير' : 'Last Name'} *
              </label>
              <Input
                value={newUserData.last_name}
                onChange={(e) => setNewUserData({ ...newUserData, last_name: e.target.value })}
                placeholder={language === 'ar' ? 'محمد' : 'Mohammed'}
                className="bg-white/5 border-white/10 text-white focus:border-emerald-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {language === 'ar' ? 'البريد الإلكتروني' : 'Email'} *
            </label>
            <Input
              type="email"
              value={newUserData.email}
              onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
              placeholder="example@email.com"
              className="bg-white/5 border-white/10 text-white focus:border-emerald-500"
              dir="ltr"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#71717A] mb-2">
              {language === 'ar' ? 'رقم الهاتف' : 'Phone'} ({language === 'ar' ? 'اختياري' : 'Optional'})
            </label>
            <Input
              type="tel"
              value={newUserData.phone}
              onChange={(e) => setNewUserData({ ...newUserData, phone: e.target.value })}
              placeholder="+971 50 123 4567"
              className="bg-white/5 border-white/10 text-white focus:border-emerald-500"
              dir="ltr"
            />
          </div>
          <div className="p-3 bg-emerald-500/10 rounded-xl border border-emerald-500/20">
            <p className="text-xs text-emerald-400">
              {language === 'ar'
                ? '💡 سيتم إرسال بريد إلكتروني للعميل يحتوي على رابط لإنشاء كلمة المرور وتفعيل الحساب'
                : '💡 An email will be sent to the customer with a link to create a password and activate the account'}
            </p>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} className="border-white/10 text-white hover:bg-white/5">
            {language === 'ar' ? 'إلغاء' : 'Cancel'}
          </Button>
          <Button
            onClick={onSubmit}
            disabled={processingUser === 'new' || !newUserData.first_name || !newUserData.last_name || !newUserData.email}
            className="bg-emerald-500 hover:bg-emerald-600 text-white"
            data-testid="user-add-submit-btn"
          >
            {processingUser === 'new' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin me-2" />
                {language === 'ar' ? 'جاري الإنشاء...' : 'Creating...'}
              </>
            ) : (
              <>
                <UserPlus className="h-4 w-4 me-2" />
                {language === 'ar' ? 'إنشاء الحساب' : 'Create Account'}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ResetLinkModal({ open, onOpenChange, resetLinkData, onClose }) {
  if (!resetLinkData) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="bg-[#1a1a2e] border border-[#2a2a4e] text-white" />
      </Dialog>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1a1a2e] border border-[#2a2a4e] text-white" data-testid="user-reset-link-modal">
        <DialogHeader>
          <DialogTitle className="text-cyan-400 flex items-center gap-2">
            <Link2 className="h-5 w-5" />
            رابط إعادة تعيين كلمة المرور
          </DialogTitle>
          <DialogDescription className="text-[#808080]">
            تم إنشاء رابط مؤقت لإعادة تعيين كلمة المرور - صالح لمدة 24 ساعة
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="bg-[#0a0a0a] border border-[#2a2a4e] rounded-xl p-4">
            <label className="block text-sm font-medium text-[#c9a227] mb-2">الرابط</label>
            <div className="flex items-center gap-2">
              <Input
                value={`${window.location.origin}${resetLinkData.reset_link}`}
                readOnly
                className="bg-[#1a1a2e] border-[#2a2a4e] text-white font-mono text-sm"
              />
              <Button
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}${resetLinkData.reset_link}`);
                  toast.success('تم نسخ الرابط');
                }}
                className="bg-[#c9a227] hover:bg-[#b8922a] text-black"
              >
                نسخ
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            {resetLinkData.user_email && (
              <div>
                <span className="text-[#808080]">البريد:</span>
                <span className="text-white ms-2">{resetLinkData.user_email}</span>
              </div>
            )}
            {resetLinkData.user_phone && (
              <div>
                <span className="text-[#808080]">الهاتف:</span>
                <span className="text-white ms-2" dir="ltr">
                  {resetLinkData.user_phone}
                </span>
              </div>
            )}
          </div>
          <p className="text-xs text-amber-400 bg-amber-500/10 p-3 rounded-lg">
            يمكنك إرسال هذا الرابط للمستخدم عبر البريد الإلكتروني أو WhatsApp
          </p>
        </div>
        <DialogFooter>
          <Button onClick={onClose} className="bg-[#c9a227] hover:bg-[#b8922a] text-black">
            تم
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
